import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CloudOff, RefreshCw, CheckCircle2, Loader2 } from "lucide-react";
import { useOfflineQueue } from "@/lib/offlineSync";
import { API } from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";

/**
 * Compact banner shown above answer-submitting pages. Surfaces:
 *   - Number of answers queued offline
 *   - Online/offline state
 *   - Manual "Sync now" button (uses the same flush logic the SW does)
 *
 * Hides itself entirely when the queue is empty AND the device is online.
 */
export default function OfflineSyncBanner() {
  const { pending, isOnline, flushNow } = useOfflineQueue(API);
  const [busy, setBusy] = useState(false);

  if (pending === 0 && isOnline) return null;

  const sync = async () => {
    setBusy(true);
    try {
      const r = await flushNow();
      if (r?.synced > 0) toast.success(`Synced ${r.synced} offline answer${r.synced > 1 ? "s" : ""}`);
      else if (r?.failed > 0) toast.error("Sync failed — will retry when online");
    } finally { setBusy(false); }
  };

  return (
    <Card
      className={`p-3 border ${isOnline ? "border-amber-200 bg-amber-50" : "border-slate-300 bg-slate-50"} flex items-center gap-3 flex-wrap`}
      data-testid="offline-sync-banner"
    >
      <div className={`h-9 w-9 rounded-md grid place-items-center ${isOnline ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"} shrink-0`}>
        {isOnline ? <RefreshCw className="w-4 h-4" strokeWidth={1.75} /> : <CloudOff className="w-4 h-4" strokeWidth={1.75} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
          {isOnline ? "Pending sync" : "You're offline"}
          {pending > 0 && (
            <Badge variant="outline" className={`${isOnline ? "bg-white text-amber-800 border-amber-300" : "bg-white text-slate-700 border-slate-300"} text-[10px] font-bold`} data-testid="offline-pending-count">
              {pending} queued
            </Badge>
          )}
        </div>
        <div className="text-[11px] text-slate-600 mt-0.5">
          {isOnline
            ? "Your offline answers will sync automatically — or tap Sync now."
            : "Your answers are being saved locally and will sync the moment you reconnect."}
        </div>
      </div>
      {isOnline && pending > 0 && (
        <Button size="sm" variant="outline" onClick={sync} disabled={busy} className="border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="offline-sync-now-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
          Sync now
        </Button>
      )}
    </Card>
  );
}
